import React, { useEffect, useMemo, useState } from "react";
import { Dialog, Box, Divider } from "@mui/material";
import styled from "styled-components";
import {
    FlexContainer,
    Toast,
    Text, LottieLoadingIndicator
} from "src/components";
import { SocialMediaPlatform } from "src/scenes/Noticeboard/components/AccountLinking/utils/Enums";
import LinkedSocialPage from "src/scenes/Noticeboard/components/NoticeboardLinkedSocialItems";

const ConnectionStep = {
    queryingAccounts: "query",
    choosingSelections: "choose",
    savingSelections: "save"
};

const ActionButton = styled.button`
    width: 140px;
    height: 38px;
    border-radius: 7px;
    box-shadow: 0px 3px 6px #00000029;
    font: normal normal medium 14px/16px Manrope;
    letter-spacing: 0px;
    text-align: center;
    opacity: 1;
    cursor: pointer;
    margin-right: 15px;
    border: ${({ variant }) => (variant === "cancel" ? "1px solid #000000" : "none")};
    background: ${({ variant }) => (variant === "cancel" ? "#FFFFFF" : "#171A22")};
    color: ${({ variant }) => (variant === "cancel" ? "#000000" : "#FFFFFF")};

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

/**
 * @param {{
 *     organisation: { sub: string, name: string, picture: string | null },
 *     isSelected: boolean,
 *     onToggle: () => {}
 * }} props
 */
const OrganisationItemComponent = ({
    organisation,
    isSelected,
    onToggle
}) => {
    return (
        <LinkedSocialPage
            pageName={organisation.name}
            pagePhoto={organisation.picture}
            pagePlatformType={SocialMediaPlatform.LINKEDIN}
            pageUuid={organisation.sub}
            onToggle={onToggle}
            checked={isSelected}
            isPosted={false}
            isEditForm={false}
            scheduleDateInTheFuture={false}
            showToggleSwitch={true}
        />
    );
};

export const FinaliseLinkedInModal = ({
    code,
    setConnectingLinkedInAccount,
    queryAvailableLinkedInOrganisations,
    saveLinkedInDetails,
    dismissLinkedInLinking
}) => {

    const [step, setStep] = useState(ConnectionStep.queryingAccounts);
    const [availableOrganisations, setAvailableOrganisations] = useState([]);
    const [selectedOrganisationIds, setSelectedOrganisationIds] = useState([]);
    const [longLivedTokenDetails, setLongLivedTokenDetails] = useState(null);

    const isLoading = useMemo(() => {
        return step === ConnectionStep.queryingAccounts || step === ConnectionStep.savingSelections;
    }, [step]);

    const isSelected = organisationId => {
        return selectedOrganisationIds.some(id => id === organisationId);
    };

    const toggleSelection = organisationId => {
        if (isSelected(organisationId)) {
            setSelectedOrganisationIds(selectedOrganisationIds.filter(id => id !== organisationId));
        } else {
            setSelectedOrganisationIds([...selectedOrganisationIds, organisationId]);
        }
    };

    const queryOrganisations = async () => {
        try {
            const response = await queryAvailableLinkedInOrganisations(code);
            const { organisations, longLivedToken } = response;
            if (organisations.length === 0) {
                Toast.error("Your LinkedIn user does not manage any organisations.");
                dismissLinkedInLinking();
                return;
            }
            setAvailableOrganisations(organisations);
            setSelectedOrganisationIds(organisations.map(organisation => organisation.sub));
            setLongLivedTokenDetails(longLivedToken);
            setStep(ConnectionStep.choosingSelections);
        } catch (error) {
            console.error(error);
            Toast.error("An error prevented looking up your organisations.");
            setConnectingLinkedInAccount(false);
        }
    };

    const saveSelections = async () => {
        setStep(ConnectionStep.savingSelections);
        saveLinkedInDetails(code, selectedOrganisationIds, longLivedTokenDetails);
    };

    useEffect(() => {
        queryOrganisations();
    }, []);

    return (
        <Dialog
            open={true}
            maxWidth="md"
            scroll="body"
            PaperProps={{ style: { width: 650, height: "auto", borderRadius: 15, overflow: "auto" } }}
        >
            { isLoading &&
                <FlexContainer width="100%" tWidth="100%" mWidth="100%" minHeight="512px" alignItems="center" justifyContent="center">
                    <LottieLoadingIndicator />
                </FlexContainer>
            }
            { !isLoading &&
                <>
                    <Box sx={{ padding: "1.563rem 3.125rem 0.625rem" }}>
                        <Text size="40px" weight="700" align="left" margin="0" color="#11141A">
                            Connect LinkedIn Pages
                        </Text>
                    </Box>
                    <Box sx={{ padding: ".805rem 3.125rem", marginTop: "-20px" }}>
                        <Text size="1rem" align="left" color="#8551AE">
                           We’ve retrieved your LinkedIn Pages. Turn on the Pages you want to connect and manage through Me Business. 
                           You can update your selection by reconnecting you Linkedin account again.
                        </Text>
                    </Box>
                    <Divider />
                    <Box sx={{ padding: "1.75rem 3.125rem" }}>
                        { availableOrganisations.map(organisation => (
                            <OrganisationItemComponent
                                key={organisation.sub}
                                organisation={organisation}
                                isSelected={isSelected(organisation.sub)}
                                onToggle={toggleSelection}
                            />
                        ))}
                    </Box>
                    <Divider />
                    <Box sx={{ padding: "1.25rem 1.125rem 2.188rem" }}>
                        <div style={{ display: "flex", flexDirection: "row", justifyContent: "end" }}>
                            <ActionButton variant="cancel" onClick={dismissLinkedInLinking}>Cancel</ActionButton>
                            <ActionButton variant="save" onClick={saveSelections}>Connect & Save</ActionButton>
                        </div>
                    </Box>
                </>
            }
        </Dialog>
    );
};
